import { useEffect, useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useReceipts, type ReceiptFilters } from "../hooks/useReceipts";
import { api } from "../api/client";
import type { Receipt, Category } from "../types";
import { TopNav } from "../components/layout/TopNav";
import { SummaryBar } from "../components/layout/SummaryBar";
import { ReceiptList } from "../components/receipts/ReceiptList";
import { ReceiptDetail } from "../components/receipts/ReceiptDetail";
import { UploadModal } from "../components/receipts/UploadModal";
import { useIsMobile } from "../hooks/useIsMobile";

export function DashboardPage() {
  const { user, logout } = useAuth();
  const { receipts, total, loading, fetchReceipts, uploadReceipt, updateReceipt, deleteReceipt } = useReceipts();
  const [categories, setCategories] = useState<Category[]>([]);
  const [selected, setSelected] = useState<Receipt | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [filters, setFilters] = useState<ReceiptFilters>({});
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchReceipts(filters);
  }, [filters, fetchReceipts]);

  useEffect(() => {
    api.get<Category[]>("/api/categories").then(setCategories).catch(() => {});
  }, []);

  const thisMonthTotal = receipts
    .filter(r => {
      if (!r.receipt_date) return false;
      const d = new Date(r.receipt_date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((sum, r) => sum + (r.amount ? parseFloat(r.amount) : 0), 0);

  const filteredTotal = receipts.reduce((sum, r) => sum + (r.amount ? parseFloat(r.amount) : 0), 0);

  const handleUploadAndSave = useCallback(async (file: File): Promise<Receipt> => {
    const receipt = await uploadReceipt(file);
    return receipt;
  }, [uploadReceipt]);

  const handleSaveAfterUpload = useCallback(async (id: string, updates: Partial<Receipt>): Promise<Receipt> => {
    const updated = await updateReceipt(id, updates);
    fetchReceipts(filters);
    return updated;
  }, [updateReceipt, fetchReceipts, filters]);

  if (!user) return null;

  return (
    <div style={{ minHeight: "100vh", background: "#0f172a" }}>
      <TopNav user={user} onLogout={logout} onUpload={() => setShowUpload(true)} hideUpload={isMobile} />
      <SummaryBar total={filteredTotal} thisMonth={thisMonthTotal} count={total} />

      <div style={{ padding: "1rem 1.5rem" }}>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" as const : "row" as const, gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          <input
            placeholder="Search merchant…"
            style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "4px", color: "#f1f5f9", padding: "0.375rem 0.75rem", fontSize: "0.875rem", width: isMobile ? "100%" : undefined }}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
          />
          <select
            style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "4px", color: "#f1f5f9", padding: "0.375rem 0.75rem", fontSize: "0.875rem", width: isMobile ? "100%" : undefined }}
            onChange={e => setFilters(f => ({ ...f, category_id: e.target.value || undefined, page: 1 }))}
          >
            <option value="">All categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "4px", color: "#f1f5f9", padding: "0.375rem 0.75rem", width: isMobile ? "100%" : undefined }}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value || undefined, page: 1 }))} />
          <input type="date" style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "4px", color: "#f1f5f9", padding: "0.375rem 0.75rem", width: isMobile ? "100%" : undefined }}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value || undefined, page: 1 }))} />
        </div>

        <div style={{ background: "#0f172a", borderRadius: "8px", border: "1px solid #1e293b" }}>
          <ReceiptList receipts={receipts} categories={categories} loading={loading} onSelect={setSelected} />
        </div>
      </div>

      {selected && (
        <ReceiptDetail
          receipt={selected}
          categories={categories}
          onUpdate={async (id, updates) => { const r = await updateReceipt(id, updates); fetchReceipts(filters); return r; }}
          onDelete={async (id) => { await deleteReceipt(id); setSelected(null); }}
          onClose={() => setSelected(null)}
        />
      )}

      {showUpload && (
        <UploadModal
          onUpload={handleUploadAndSave}
          onSave={handleSaveAfterUpload}
          onClose={() => { setShowUpload(false); fetchReceipts(filters); }}
          categories={categories}
        />
      )}

      {isMobile && (
        <button
          onClick={() => setShowUpload(true)}
          style={{
            position: "fixed", bottom: "1.5rem", right: "1.5rem",
            width: "56px", height: "56px", borderRadius: "50%",
            background: "#1d4ed8", color: "#fff", border: "none",
            cursor: "pointer", zIndex: 40,
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          aria-label="Upload receipt"
        >
          <Plus size={26} />
        </button>
      )}
    </div>
  );
}
